import React from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import {
  LightModeRounded as LightModeIcon,
  DarkModeRounded as DarkModeIcon,
  SettingsBrightnessRounded as SystemModeIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { setTheme, selectTheme } from '@/store/slices/uiSlice';

export const ThemeToggle: React.FC = React.memo(() => {
  const { t } = useTranslation(['common'], { useSuspense: false });
  const dispatch = useAppDispatch();
  const currentTheme = useAppSelector(selectTheme);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleThemeChange = (theme: 'light' | 'dark' | 'system') => {
    dispatch(setTheme(theme));
    handleMenuClose();
  };

  const getCurrentIcon = () => {
    switch (currentTheme) {
      case 'light':
        return <LightModeIcon />;
      case 'dark':
        return <DarkModeIcon />;
      case 'system':
        return <SystemModeIcon />;
      default:
        return <SystemModeIcon />;
    }
  };

  const getTooltipText = () => {
    switch (currentTheme) {
      case 'light':
        return t('theme.lightTooltip');
      case 'dark':
        return t('theme.darkTooltip');
      case 'system':
        return t('theme.systemTooltip');
      default:
        return t('theme.defaultTooltip');
    }
  };

  return (
    <>
      <Tooltip title={getTooltipText()}>
        <IconButton
          color="inherit"
          onClick={handleMenuOpen}
          aria-label={t('theme.selector')}
          aria-expanded={open ? 'true' : undefined}
          aria-haspopup="menu"
        >
          {getCurrentIcon()}
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            minWidth: 180,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem
          onClick={() => handleThemeChange('light')}
          selected={currentTheme === 'light'}
        >
          <ListItemIcon>
            <LightModeIcon color={currentTheme === 'light' ? 'primary' : 'inherit'} />
          </ListItemIcon>
          <ListItemText>{t('theme.light')}</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => handleThemeChange('dark')}
          selected={currentTheme === 'dark'}
        >
          <ListItemIcon>
            <DarkModeIcon color={currentTheme === 'dark' ? 'primary' : 'inherit'} />
          </ListItemIcon>
          <ListItemText>{t('theme.dark')}</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => handleThemeChange('system')}
          selected={currentTheme === 'system'}
        >
          <ListItemIcon>
            <SystemModeIcon color={currentTheme === 'system' ? 'primary' : 'inherit'} />
          </ListItemIcon>
          <ListItemText>{t('theme.system')}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
});

ThemeToggle.displayName = 'ThemeToggle';

export default ThemeToggle;